import { zodResolver } from "@hookform/resolvers/zod";
import type { ComponentProps } from "react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { enqueueSnackbar } from "../../../../../components/GlobalSnackbar/snackbarStore";
import { isSupabaseError } from "../../../../../constants/supabaseErrors";
import { formSchema as formSchemaBase } from "../types";
import useSubjectActionModal from "../useSubjectActionModal";
import type AddQuoteReceivedActionModal from "./AddQuoteReceivedActionModal";

const formSchema = formSchemaBase.extend({
  date: z.date('Ce champ est obligatoire').transform(val => val.toISOString()),
  provider_id: z.string().nullable().optional(),
});

type FormInputs = z.input<typeof formSchema>;
type FormValues = z.infer<typeof formSchema>;

type Props = ComponentProps<typeof AddQuoteReceivedActionModal>;

export default function useAddQuoteReceivedActionModal(props: Props) {
  const [serverError, setServerError] = useState<string | null>(null);

  const initialDate = props.item?.date
    ? new Date(props.item.date)
    : props.initialValues?.date
    ? new Date(props.initialValues.date)
    : new Date();

  const form = useForm<FormInputs, unknown, FormValues>({
    mode: "onChange",
    defaultValues: {
      date: initialDate,
      amount: props.item?.amount ?? (props.initialValues?.amount != null ? props.initialValues.amount : undefined),
      comment: props.item?.comment || props.initialValues?.comment || '',
      provider_id: props.item?.provider_id ?? props.initialValues?.provider_id ?? null,
      documents: props.initialValues?.documents ?? [],
    },
    resolver: zodResolver(formSchema),
  });

  const {
    isDocumentLoading,
    isLoading,
    handleCallApi,
  } = useSubjectActionModal(
    "quote_received",
    props.item?.id ?? undefined,
    props.subjectId,
    form,
  );

  useEffect(() => {
    if (props.initialValues) {
      if (props.initialValues.date) {
        form.setValue("date", new Date(props.initialValues.date));
      }
      if (props.initialValues.amount !== undefined) {
        form.setValue("amount", props.initialValues.amount != null ? props.initialValues.amount : undefined);
      }
      if (props.initialValues.comment !== undefined) {
        form.setValue("comment", props.initialValues.comment);
      }
      if (props.initialValues.provider_id !== undefined) {
        form.setValue("provider_id", props.initialValues.provider_id);
      }
      if (props.initialValues.documents) {
        form.setValue("documents", props.initialValues.documents);
      }
    }
  }, [props.initialValues, form]);

  async function handleConfirm(data: FormValues) {
    setServerError(null);

    try {
      await handleCallApi(data);

      enqueueSnackbar(props.item ? "Action mise à jour." : "Réception de devis enregistrée.", "success");
      props.onSuccess?.();
      props.onClose();
    } catch (error: unknown) {
      if (isSupabaseError(error)) {
        setServerError(error.message);
        return;
      }

      setServerError(
        "Une erreur est survenue",
      );
    }
  }

  return {
    form,
    handleConfirm,
    isLoading,
    serverError,
    isDocumentLoading,
  };
}
